import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Table,
  InputNumber,
  Divider,
  Alert,
  Button,
  Space,
  Row,
  Col,
  Typography,
  message,
  Spin,
  Card,
  Badge,
  Statistic
} from 'antd';
import {
  RollbackOutlined,
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { BASE_URL } from '../utils/constants';

const { Text, Title } = Typography;

const ModalRectificativa = ({
  visible,
  factura, // { serie, nfactura, ejercicio, total, agencia, nomAgencia, nreserva, fecha }
  onCancel,
  onSuccess,
  codigoHotel = 1
}) => {
  // Estados
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detalles, setDetalles] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [importeTotal, setImporteTotal] = useState(0);
  const [comentario, setComentario] = useState('');
  const [pendienteRectificar, setPendienteRectificar] = useState(0);
  const [totalRectificado, setTotalRectificado] = useState(0);
  const [numRectificativas, setNumRectificativas] = useState(0);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (visible && factura) {
      cargarDatosFactura();
    }
  }, [visible, factura]);

  const cargarDatosFactura = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${BASE_URL}/api/facturas-rectificativas/factura/${factura.serie}/${factura.ejercicio}/${factura.nfactura}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { codigoHotel }
        }
      );
      
      const data = response.data.data;
    //   console.log(" data => ",data);
      
      setDetalles(data.detalles || []);
      setPendienteRectificar(data.rectificativas?.pendiente || 0);
      setTotalRectificado(-data.rectificativas?.total || 0);
      setNumRectificativas(data.rectificativas?.count || 0);
      
      // Inicializar líneas a rectificar
    //   console.log(" data.detalles => ",data.detalles);
      
      const lineasIniciales = (data.detalles || []).map(detalle => ({
        seccion: detalle.Seccion || '',
        cargo: detalle.Cargo || '',
        tipo: detalle.Tipo || '',
        total: detalle.Total || 0,
        importe: 0,
        seleccionado: false,
        i:detalle.i
      }));
      
      setLineas(lineasIniciales);
      setImporteTotal(0);
      setComentario('');
      
    } catch (error) {
      console.error('❌ Error cargando datos de factura:', error);
      message.error('Error al cargar los datos de la factura');
    } finally {
      setLoading(false);
    }
  };

  const handleCambioImporte = (index, valor) => {
    const nuevasLineas = [...lineas];
    const importe = parseFloat(valor) || 0;
    nuevasLineas[index].importe = importe;
    nuevasLineas[index].seleccionado = importe > 0;
    setLineas(nuevasLineas);
    
    const total = nuevasLineas.reduce((sum, l) => sum + l.importe, 0);
    setImporteTotal(total);
  };

  const handleSeleccionarTodas = (checked) => {
    const nuevasLineas = lineas.map(l => ({
      ...l,
      importe: checked ? l.total : 0,
      seleccionado: checked
    }));
    setLineas(nuevasLineas);
    
    const total = nuevasLineas.reduce((sum, l) => sum + l.importe, 0);
    setImporteTotal(total);
  };

  const handleGuardar = async () => {
    // Validaciones
    const lineasSeleccionadas = lineas.filter(l => l.importe > 0);
    if (lineasSeleccionadas.length === 0) {
      message.warning('Debe seleccionar al menos una línea para rectificar');
      return;
    }
    
    if (importeTotal > pendienteRectificar + 0.01) {
      message.error(`El importe a rectificar (${importeTotal.toFixed(2)}) supera el pendiente (${pendienteRectificar.toFixed(2)})`);
      return;
    }
    
    if (!comentario.trim()) {
      message.warning('Debe ingresar un comentario para la rectificación');
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      console.log(" lineasSeleccionadas => ",lineasSeleccionadas);
      
      const payload = {
        serieOriginal: factura.serie,
        ejercicioOriginal: factura.ejercicio,
        nfacturaOriginal: factura.nfactura,
        codigoHotel: parseInt(codigoHotel),
        importeTotal: importeTotal,
        comentario: comentario.trim(),
        lineas: lineasSeleccionadas.map(l => ({
          seccion: l.seccion,
          cargo: l.cargo,
          importe: l.importe,
          tipo: l.tipo,
          total: l.total,
          i:l.i
        })),
        metodoRectifica: '02'
      };
      
      const response = await axios.post(
        `${BASE_URL}/api/facturas-rectificativas/rectificar`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      message.success(`Factura rectificativa creada: ${response.data.data.serie}-${response.data.data.nfactura}`);
      
      if (onSuccess) {
        onSuccess(response.data.data);
      }
      
      // Cerrar modal
      onCancel();
      
    } catch (error) {
      console.error('❌ Error creando rectificativa:', error);
      message.error(error.response?.data?.error || 'Error al crear la factura rectificativa');
    } finally {
      setSaving(false);
    }
  };

  // Columnas de la tabla de líneas
  const columnas = [
    {
      title: 'Sección',
      dataIndex: 'seccion',
      key: 'seccion',
      width: 100,
      render: (seccion) => seccion || '—',
    },
    {
      title: 'Cargo',
      dataIndex: 'cargo',
      key: 'cargo',
      width: 100,
      render: (cargo) => cargo || '—',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 150,
      render: (tipo) => tipo || '—',
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (total) => (
        <Text strong style={{ color: '#1890ff' }}>
          {total.toFixed(2)} €
        </Text>
      ),
    },
    {
      title: 'Importe a Rectificar',
      dataIndex: 'importe',
      key: 'importe',
      width: 180,
      align: 'right',
      render: (importe, record, index) => (
        <InputNumber
          value={importe}
          onChange={(value) => handleCambioImporte(index, value)}
          min={0}
          max={record.total}
          step={0.01}
          precision={2}
          style={{ width: '100%' }}
          placeholder="0.00"
          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={value => parseFloat(value.replace(/,/g, '')) || 0}
          disabled={loading || saving}
        />
      ),
    },
  ];

  // Verificar si todas las líneas están seleccionadas
  const todasSeleccionadas = lineas.length > 0 && lineas.every(l => l.seleccionado);
  const algunaSeleccionada = lineas.some(l => l.seleccionado);

  // Calcular si se puede rectificar
  const puedeRectificar = importeTotal > 0 && 
                          importeTotal <= pendienteRectificar + 0.01 && 
                          comentario.trim().length > 0;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <RollbackOutlined style={{ fontSize: 20, color: '#fa8c16', marginRight: 12 }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            Rectificar Factura {factura?.serie || ''}-{factura?.nfactura || ''}
          </span>
          <Badge 
            status={pendienteRectificar > 0.01 ? 'processing' : 'success'} 
            text={pendienteRectificar > 0.01 ? `${pendienteRectificar.toFixed(2)} € pendiente` : 'Completada'}
            style={{ marginLeft: 16 }}
          />
        </div>
      }
      open={visible}
      onOk={handleGuardar}
      onCancel={onCancel}
      width={820}
      okText="Crear Rectificativa"
      cancelText="Cancelar"
      confirmLoading={saving}
      okButtonProps={{ 
        disabled: !puedeRectificar || loading || saving,
        style: { backgroundColor: '#fa8c16', borderColor: '#fa8c16' }
      }}
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '20px 24px' }}
    >
      <Spin spinning={loading || saving}>
        {/* Cabecera de la factura original */}
        <Card 
          size="small" 
          style={{ 
            marginBottom: 16,
            backgroundColor: '#fafafa',
            borderRadius: 8
          }}
        >
          <Row gutter={[16, 8]}>
            <Col span={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Nº Factura</Text>
              <div>
                <Text strong style={{ fontSize: 16 }}>
                  {factura?.serie || ''}-{factura?.nfactura || ''}
                </Text>
              </div>
            </Col>
            <Col span={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Fecha</Text>
              <div>
                <Text>{factura?.fecha ? new Date(factura.fecha).toLocaleDateString() : '—'}</Text>
              </div>
            </Col>
            <Col span={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Agencia</Text>
              <div>
                <Text>{factura?.agencia || '—'} {factura?.nomAgencia || ''}</Text>
              </div>
            </Col>
            <Col span={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Reserva</Text>
              <div>
                <Text>{factura?.nreserva || '—'}</Text>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Resumen de rectificaciones */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Statistic 
              title="Total Factura" 
              value={factura?.total || 0}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: 18 }}
              suffix="€"
            />
          </Col>
          <Col span={8}>
            <Statistic 
              title="Ya Rectificado" 
              value={totalRectificado || 0}
              precision={2}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#fa8c16', fontSize: 18 }}
              suffix="€"
            />
          </Col>
          <Col span={8}>
            <Statistic 
              title="Pendiente de Rectificar" 
              value={pendienteRectificar || 0}
              precision={2}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: pendienteRectificar > 0 ? '#52c41a' : '#999', fontSize: 18 }}
              suffix="€"
            />
          </Col>
        </Row>

        {numRectificativas > 0 && (
          <Alert
            message={`Ya existen ${numRectificativas} rectificativa${numRectificativas > 1 ? 's' : ''} sobre esta factura`}
            description={`Total rectificado: ${totalRectificado.toFixed(2)} € de ${factura?.total?.toFixed(2) || '0.00'} €`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Divider style={{ margin: '12px 0' }}>
          <span style={{ fontSize: 14, fontWeight: 500 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            Líneas a Rectificar
          </span>
        </Divider>

        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button 
              size="small" 
              onClick={() => handleSeleccionarTodas(!todasSeleccionadas)}
              disabled={loading || saving || lineas.length === 0}
            >
              {todasSeleccionadas ? 'Deseleccionar todas' : 'Seleccionar todas'}
            </Button>
            {algunaSeleccionada && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                <InfoCircleOutlined style={{ marginRight: 4 }} />
                Total a rectificar: <Text strong style={{ color: '#fa8c16', fontSize: 15 }}>
                  {importeTotal.toFixed(2)} €
                </Text>
              </Text>
            )}
          </Space>
          {importeTotal > pendienteRectificar + 0.01 && (
            <Text type="danger" style={{ fontSize: 13 }}>
              <ExclamationCircleOutlined /> El importe excede el pendiente 
            </Text>
          )}
        </div>

        <Table
          dataSource={lineas}
          rowKey={(item, index) => index}
          size="small"
          pagination={false}
          columns={columnas}
          loading={loading}
          locale={{ emptyText: 'No se encontraron líneas para rectificar' }}
          scroll={{ y: 280 }}
          style={{ marginBottom: 16 }}
          rowClassName={(record) => record.importe > 0 ? 'ant-table-row-selected' : ''}
        />

        <Divider style={{ margin: '12px 0' }}>
          <span style={{ fontSize: 14, fontWeight: 500 }}>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            Comentario de Rectificación
          </span>
        </Divider>

        <Input.TextArea
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          placeholder="Ej: Rectificación por anulación de cargo no correspondiente..."
          rows={3}
          maxLength={500}
          showCount
          disabled={loading || saving}
          style={{ marginBottom: 16 }}
        />

        <Alert
          message="La factura rectificativa se creará con importes negativos"
          description="Se generará una nueva factura en la serie configurada para rectificativas, anulando los importes seleccionados de la factura original."
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
        />
      </Spin>

      {/* Estilos adicionales para la tabla */}
      <style jsx="true">{`
        .ant-table-row-selected {
          background-color: #f6ffed !important;
        }
        .ant-table-row-selected td {
          background-color: #f6ffed !important;
        }
      `}</style>
    </Modal>
  );
};

export default ModalRectificativa;